<template>
    <div class="profile-container">
        <!-- Header do Perfil -->
        <PageHeader
            :title="userData.name || 'Usuário'"
            icon="fas fa-user-circle"
            :sub-title="userData.email || 'Email não informado'"
        />

        <!-- Conteúdo Principal -->

        <div class="row">
            <div class="col-md-12">
                <div class="card">
                    <CardHeader title="Meu Perfil"/>
                    <div class="card-body">
                        <div class="section-form">
                            <div class="row">
                                <div class="col-md-10">
                                    <form @submit.prevent="save">
                                        <!-- Informações Básicas -->
                                        <UserBasicInfo
                                            :value="userData"
                                            :errors="errors"
                                            :disabled-fields="disabledFields"
                                            @update="updateUserData"
                                        />

                                        <!-- Informações de Contato -->
                                        <UserContactInfo
                                            :value="userData"
                                            :errors="errors"
                                            :disabled-fields="disabledFields"
                                            @update="updateUserData"
                                        />

                                        <!-- Informações de Motorista/CNH -->
                                        <UserDriverInfo
                                            :disabled-fields="disabledFields"
                                            :value="userData"
                                            :errors="errors"
                                            @update="updateUserData"
                                        />

                                        <!-- Seção de Senha (apenas para usuários internos) -->
                                        <UserPasswordSection
                                            v-if="shouldShowPasswordFields"
                                            :value="userData"
                                            :is-editing="true"
                                            :errors="errors"
                                            @update="updateUserData"
                                        />

                                        <!-- Botões de Ação -->
                                        <div class="form-actions">
                                            <router-link
                                                to="/"
                                                class="btn btn-back"
                                            >
                                                <i class="fa fa-arrow-left mr-2"></i>
                                                Voltar
                                            </router-link>

                                            <button
                                                type="submit"
                                                class="btn btn-primary"
                                                :disabled="isLoading"
                                            >
                                                <i
                                                    class="fa fa-spinner fa-spin mr-2"
                                                    v-if="isLoading"
                                                ></i>
                                                <i
                                                    class="fa fa-save mr-2"
                                                    v-else
                                                ></i>
                                                {{ isLoading ? 'Salvando...' : 'Salvar' }}
                                            </button>
                                        </div>
                                    </form>
                                </div>

                                <!-- Upload de Foto -->
                                <div class="col-md-2">
                                    <UserPhotoUpload
                                        :value="userData"
                                        :preview-image="previewImage"
                                        @upload-image="handleImageUpload"
                                        @update="updateUserData"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Loading Overlay -->
        <Loading
            :active.sync="isLoading"
            :is-full-page="fullPage"
        />
    </div>
</template>

<script>
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import {getMessage} from '../../composables/message'
import {convertDateFromIso, convertDateToIso} from '../../composables/functions'

// Importação dos componentes modulares
import UserBasicInfo from './form-components/UserBasicInfo.vue'
import UserContactInfo from './form-components/UserContactInfo.vue'
import UserDriverInfo from './form-components/UserDriverInfo.vue'
import UserPasswordSection from './form-components/UserPasswordSection.vue'
import UserPhotoUpload from './form-components/UserPhotoUpload.vue'
import CardHeader from "../Base/CardHeader.vue";
import PageHeader from "../Base/PageHeader.vue";

export default {
    name: 'Profile', // Nome do componente: Perfil do Usuário
    components: {
        PageHeader,
        CardHeader,
        Loading,
        UserBasicInfo,
        UserContactInfo,
        UserDriverInfo,
        UserPasswordSection,
        UserPhotoUpload,
    },
    data() {
        return {
            isLoading: false, // isLoading - controla o estado de carregamento
            fullPage: true, // fullPage - controla se o loading é em tela cheia
            previewImage: null, // previewImage - imagem de preview do upload
            errors: {}, // errors - erros de validação do formulário
            costCenters: [], // costCenters - lista de centros de custo

            // userData - dados do usuário
            userData: {
                name: '',
                matriculation: '',
                rg: '',
                cpf: '',
                email: '',
                cellphone: '',
                date_of_birth: '',
                admission_date: '',
                cost_center_id: null,
                number_cnh: '',
                validity_cnh: '',
                pri_cnh: '',
                password: '',
                password_confirmation: '',
                passwordEdit: '',
                photo: null,
                id: null,
            },

            // disabledFields - campos que devem ser desabilitados na edição
            disabledFields: {},
        }
    },
    computed: {
        shouldShowPasswordFields() {
            // verifica se deve mostrar campos de senha
            return this.userData.email && this.userData.email.indexOf('innovareti.com.br') > -1
        }
    },
    mounted() {
        this.loadUserData()
    },
    methods: {
        updateUserData(field, value) {
            this.$set(this.userData, field, value)
        },

        handleImageUpload(imagePreview) {
            this.previewImage = imagePreview
        },

        loadUserData() {
            if (!this.$store.state.user) {
                setTimeout(() => {
                    this.loadUserData();
                }, 300)
            } else {
                const user = this.$store.state.user

                if (user) {
                    Object.assign(this.userData, {
                        ...user,
                        date_of_birth: convertDateFromIso(user.date_of_birth),
                        admission_date: convertDateFromIso(user.admission_date),
                        validity_cnh: convertDateFromIso(user.validity_cnh),
                        pri_cnh: convertDateFromIso(user.pri_cnh),
                        passwordEdit: '',
                        password_confirmation: '',
                    })

                    // Configura campos desabilitados baseado nos dados existentes
                    this.disabledFields.cpf = user.cpf !== null && user.cpf !== ''
                    this.disabledFields.date_of_birth = user.date_of_birth !== null && user.date_of_birth !== ''
                    this.disabledFields.cost_center_id = user.cost_center_id !== null && user.cost_center_id !== ''
                    this.disabledFields.name = user.name !== null && user.name !== ''
                    this.disabledFields.matriculation = user.matriculation !== null && user.matriculation !== ''
                    this.disabledFields.rg = user.rg !== null && user.rg !== ''
                    this.disabledFields.admission_date = user.admission_date !== null && user.admission_date !== ''
                    this.disabledFields.pri_cnh = user.pri_cnh !== null && user.pri_cnh !== ''
                    this.disabledFields.validity_cnh = user.validity_cnh !== null && user.validity_cnh !== ''
                    this.disabledFields.number_cnh = user.number_cnh !== null && user.number_cnh !== ''
                }
            }
        },

        makeFormData() {
            // makeFormData - cria FormData para envio do formulário
            const formData = new FormData()
            const photoInput = document.getElementById('userPic')

            // Campos básicos
            formData.append('user_id', this.userData.id)
            formData.append('access_nivel', this.userData.access_nivel)
            formData.append('cost_center_id', this.userData.cost_center_id)
            formData.append('name', this.userData.name || '')
            formData.append('matriculation', this.userData.matriculation || '')
            formData.append('rg', this.userData.rg || '')
            formData.append('cpf', this.userData.cpf || '')
            formData.append('email', this.userData.email || '')
            formData.append('cellphone', this.userData.cellphone || '')
            formData.append('date_of_birth', convertDateToIso(this.userData.date_of_birth))
            formData.append('admission_date', convertDateToIso(this.userData.admission_date))

            // Campos de CNH
            formData.append('number_cnh', this.userData.number_cnh || '')
            formData.append('validity_cnh', convertDateToIso(this.userData.validity_cnh))
            formData.append('pri_cnh', convertDateToIso(this.userData.pri_cnh))

            // Centro de custo
            if (this.userData.cost_center_id) {
                const costCenterId = typeof this.userData.cost_center_id === 'object'
                    ? this.userData.cost_center_id.id
                    : this.userData.cost_center_id
                formData.append('cost_center_id', costCenterId)
            }

            // Campos de senha (apenas para usuários internos)
            if (this.shouldShowPasswordFields) {
                if (this.userData.passwordEdit && this.userData.passwordEdit.trim() !== '') {
                    formData.append('password', this.userData.passwordEdit)
                }
                if (this.userData.password_confirmation && this.userData.password_confirmation.trim() !== '') {
                    formData.append('password_confirmation', this.userData.password_confirmation)
                }
            }

            // Foto do usuário
            if (photoInput && photoInput.files[0]) {
                formData.append('photo', photoInput.files[0])
            } else {
                formData.append('photo', '')
            }

            // Métodos HTTP
            formData.append('id', this.userData.id)
            formData.append('_method', 'PATCH')

            return formData
        },

        async save() {
            // save - salva os dados do perfil do usuário
            this.isLoading = true
            this.errors = {}

            try {
                const api = this.$store.state.api + `users/${this.userData.id}`
                const formData = this.makeFormData()

                const response = await this.$http.post(api, formData)

                // Atualiza os dados do usuário no store se necessário
                if (this.$store.state.user.id === this.userData.id) {
                    this.$store.dispatch('initialize')
                }

                // Exibe mensagem de sucesso
                this.$message('Sucesso', 'Perfil atualizado com sucesso', 'success')

                // Redireciona para a página inicial
                this.$router.push('/')

            } catch (error) {
                if (error.response && error.response.data.errors) {
                    this.errors = error.response.data.errors
                }

                const message = error.response.data.errors
                    ? getMessage(error.response.data.errors)
                    : error.response.data || 'Erro ao salvar perfil'

                this.$message(null, message, 'error')
            } finally {
                this.isLoading = false
            }
        },
    },
}
</script>

<style scoped>
.profile-container {
    min-height: 100vh;
    background-color: #f8f9fa;
    padding: 20px;
}

.profile-form-section {
    background: white;
    border-radius: 8px;
}

.form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 15px;
    margin-top: 30px;
    padding-top: 25px;
    border-top: 2px solid #e9ecef;
}

/* Responsividade */
@media (max-width: 768px) {
    .profile-container {
        padding: 15px;
    }

    .form-actions {
        flex-direction: column-reverse;
        gap: 10px;
    }

    .btn {
        width: 100%;
        justify-content: center;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/User/Profile.vue